import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Users, X, Loader2, ExternalLink, Trash2 } from "lucide-react";
import { API } from "./utils";

/* ---------- Reminder List Modal ---------- */
export default function ReminderListModal({ webinar, onClose, token }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { "X-Admin-Token": token };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/webinars/${webinar.id}/reminders`,
          { headers }
        );
        setReminders(data?.reminders || []);
      } catch {
        toast.error("Konnte Opt-ins nicht laden.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Diesen Opt-in wirklich entfernen?")) return;
    try {
      await axios.delete(`${API}/admin/webinars/reminders/${id}`, { headers });
      setReminders((rs) => rs.filter((r) => r.id !== id));
      toast.success("Opt-in entfernt.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const buildWa = (r) => {
    const digits = (r.phone_norm || r.phone || "").replace(/\D+/g, "");
    const first = (r.name || "").split(" ")[0] || "Hi";
    const msg =
      `${first}, in einer Stunde startet das Webinar „${webinar.title}".\n` +
      `Hier ist dein Zugangslink: ${webinar.zoho_link}\n\n` +
      `Bis gleich — Pascal`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div
      data-testid="webinar-reminders-modal"
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Bell className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-base sm:text-lg truncate">
                WhatsApp-Reminder · {webinar.title}
              </div>
              <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {reminders.length} {reminders.length === 1 ? "Opt-in" : "Opt-ins"}
                {webinar.reminder_dispatched_at && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-bold">
                    bereits versendet
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="webinar-reminders-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 text-xs text-slate-500 bg-slate-50/50 border-b border-slate-100">
          1 Stunde vor Webinar-Start bekommst du automatisch eine Zusammenfassungs-Mail
          mit allen Opt-ins. Hier kannst du jederzeit vorab schon manuell pingen — die
          WhatsApp-Nachricht ist pro Person bereits vorausgefüllt.
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            </div>
          ) : reminders.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Bell className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="font-display font-semibold">Noch keine Opt-ins.</p>
              <p className="text-sm mt-1">
                Sobald sich jemand auf <code className="text-xs">/webinar</code> für den Reminder einträgt, erscheint er hier.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {reminders.map((r) => (
                <div
                  key={r.id}
                  data-testid={`reminder-row-${r.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-slate-900 truncate">
                      {r.name || <span className="text-slate-400 italic">ohne Name</span>}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {r.phone}
                      {r.status === "dispatched" && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-widest font-bold">
                          gepingt
                        </span>
                      )}
                    </div>
                  </div>
                  <a
                    href={buildWa(r)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`reminder-wa-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-display font-bold transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    WhatsApp
                  </a>
                  <button
                    onClick={() => remove(r.id)}
                    className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                    data-testid={`reminder-delete-${r.id}`}
                    title="Opt-in entfernen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
